import type { PreviewViewV1 } from "@enrein/shared-contracts/approval-change";
import type { ClientPortalShellViewV1 } from "@enrein/shared-contracts/client-link";
import type { V10ClientTabId } from "../types.js";
import { v10IsIntakePhase } from "./v10-intake-phase.js";

export function v10VisibleClientTabs(input: {
  readonly shell: ClientPortalShellViewV1;
  readonly preview: PreviewViewV1 | null;
  readonly isExternalProof: boolean;
}): readonly V10ClientTabId[] {
  const status = input.shell.state.status;

  if (v10IsIntakePhase(status)) {
    // Intake + clarification requests (agency may ask during intake).
    return ["intake", "requests"];
  }

  const tabs: V10ClientTabId[] = ["summary"];
  const showPreview =
    input.preview != null ||
    ["PREVIEW_READY", "CHANGE_REQUESTED", "APPROVAL_PENDING", "APPROVED"].includes(status);
  // Proof / Launch Proof is available once a reviewable preview exists — not only after approval.
  const showProof =
    input.isExternalProof ||
    [
      "PREVIEW_READY",
      "CHANGE_REQUESTED",
      "APPROVAL_PENDING",
      "APPROVED",
      "PORTAL_ACTIVE",
      "SAFE_EDIT_ACTIVE",
      "HANDOFF_READY",
    ].includes(status);
  // PREVIEW_READY must expose Changes so clients can request edits during review.
  const showChanges = [
    "PREVIEW_READY",
    "CHANGE_REQUESTED",
    "APPROVAL_PENDING",
    "SAFE_EDIT_ACTIVE",
  ].includes(status);
  // Clarification requests are available for any post-intake client link.
  const showRequests = !v10IsIntakePhase(status);
  const showHandoff = ["APPROVED", "HANDOFF_READY", "PORTAL_ACTIVE", "SAFE_EDIT_ACTIVE"].includes(status);

  if (showPreview) tabs.push("preview");
  if (showProof) tabs.push("proof");
  if (input.isExternalProof) tabs.push("record");
  if (showChanges) tabs.push("changes");
  if (showRequests) tabs.push("requests");
  if (showHandoff) tabs.push("handoff");

  return tabs;
}

export function v10DefaultClientTab(input: {
  readonly shell: ClientPortalShellViewV1;
  readonly tabIds: readonly V10ClientTabId[];
}): V10ClientTabId {
  if (v10IsIntakePhase(input.shell.state.status)) {
    return "intake";
  }
  return input.tabIds[0] ?? "summary";
}
